import React, { useMemo } from 'react';
import { Package } from 'lucide-react';
import Button from '../../common/Button.js';
import {
  emptyParcel,
  parcelFreightAmount,
  parcelsFromApi,
  recomputeParcel,
  sumChargeableWeight,
  sumParcelFreightAmount,
  sumParcelQty,
  toNum,
} from '../../../utils/cargoParcelUtils.js';
import PackageDimensionModalShell from './PackageDimensionModalShell.js';

const fmt = (value, digits = 2) => toNum(value).toFixed(digits);
const fmtKg = (value) => String(Math.round(toNum(value)));

const dimsLabel = (row) => {
  const l = toNum(row.length_cm);
  const w = toNum(row.width_cm);
  const h = toNum(row.height_cm);
  if (!l && !w && !h) return '—';
  return `${fmt(l, 2)} × ${fmt(w, 2)} × ${fmt(h, 2)} ft`;
};

const Metric = ({ label, value, accent = false }) => (
  <div className="min-w-0">
    <p className="text-[10px] sm:text-[11px] uppercase tracking-wide text-gray-400 font-medium leading-none mb-1">
      {label}
    </p>
    <p
      className={`text-xs sm:text-sm font-semibold truncate ${
        accent ? 'text-secondary' : 'text-gray-800'
      }`}
    >
      {value}
    </p>
  </div>
);

const ViewPackageDimensionModal = ({ isOpen, onClose, parcels, ratePerKg = 0 }) => {
  const rows = useMemo(() => {
    if (!Array.isArray(parcels) || parcels.length === 0) {
      return [emptyParcel(1)].map(recomputeParcel);
    }
    return parcelsFromApi(parcels);
  }, [parcels]);

  const totalQty = sumParcelQty(rows);
  const totalWeight = sumChargeableWeight(rows);
  const totalAmount = sumParcelFreightAmount(rows, ratePerKg);

  if (!isOpen) return null;

  return (
    <PackageDimensionModalShell
      title="Package Dimensions"
      onClose={onClose}
      compact
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose} className="w-full sm:w-auto">
            Close
          </Button>
        </div>
      }
    >
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4">
        <div className="rounded-lg bg-orange-50 border border-orange-100 px-2.5 py-2 text-center min-w-0">
          <p className="text-[10px] sm:text-xs text-orange-600/80 font-medium">Packages</p>
          <p className="text-sm sm:text-base font-bold text-orange-700 mt-0.5">{rows.length}</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-2 text-center min-w-0">
          <p className="text-[10px] sm:text-xs text-slate-500 font-medium">Total Qty</p>
          <p className="text-sm sm:text-base font-bold text-slate-800 mt-0.5">{totalQty}</p>
        </div>
        <div className="rounded-lg bg-blue-50 border border-blue-100 px-2.5 py-2 text-center min-w-0">
          <p className="text-[10px] sm:text-xs text-blue-600/80 font-medium">Chargeable</p>
          <p className="text-sm sm:text-base font-bold text-blue-700 mt-0.5 truncate">
            {fmtKg(totalWeight)} kg
          </p>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-2 text-center min-w-0">
          <p className="text-[10px] sm:text-xs text-emerald-600/80 font-medium">Total Amount</p>
          <p className="text-sm sm:text-base font-bold text-emerald-700 mt-0.5 truncate">
            ₹{totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </p>
        </div>
      </div>

      {/* Mobile / iPad card list */}
      <div className="space-y-2 lg:hidden">
        {rows.map((row, index) => {
          const computed = recomputeParcel(row);
          return (
            <div
              key={index}
              className="rounded-xl border border-gray-200 bg-white p-3 min-w-0"
            >
              <div className="flex items-center justify-between gap-2 mb-2.5 pb-2 border-b border-gray-100">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                    <Package className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-secondary truncate">
                      {computed.package_name || `Box ${index + 1}`}
                    </p>
                    <p className="text-[11px] text-gray-400">Package #{index + 1}</p>
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
                  Qty {computed.qty}
                </span>
              </div>

              <p className="text-xs text-gray-500 mb-2.5">
                Dimensions:{' '}
                <span className="font-medium text-gray-800">{dimsLabel(computed)}</span>
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <Metric label="Actual Wt" value={`${fmtKg(computed.actual_weight)} kg`} />
                <Metric label="Vol Wt" value={`${fmtKg(computed.volumetric_weight)} kg`} />
                <Metric label="CBM" value={fmt(computed.cbm, 3)} />
                <Metric
                  label="Chargeable"
                  value={`${fmtKg(computed.chargeable_weight)} kg`}
                  accent
                />
                <Metric
                  label="Amount"
                  value={`₹${parcelFreightAmount(computed, ratePerKg).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                  accent
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop compact table */}
      <div className="hidden lg:block rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <th className="px-3 py-2.5 text-left font-semibold">#</th>
              <th className="px-3 py-2.5 text-left font-semibold">Package</th>
              <th className="px-3 py-2.5 text-left font-semibold">L × W × H</th>
              <th className="px-3 py-2.5 text-right font-semibold">Qty</th>
              <th className="px-3 py-2.5 text-right font-semibold">Actual</th>
              <th className="px-3 py-2.5 text-right font-semibold">Vol Wt</th>
              <th className="px-3 py-2.5 text-right font-semibold">CBM</th>
              <th className="px-3 py-2.5 text-right font-semibold">Chargeable</th>
              <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, index) => {
              const computed = recomputeParcel(row);
              return (
                <tr key={index} className="hover:bg-gray-50/80">
                  <td className="px-3 py-2.5 text-gray-400 font-medium">{index + 1}</td>
                  <td className="px-3 py-2.5 font-medium text-secondary">
                    {computed.package_name || `Box ${index + 1}`}
                  </td>
                  <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                    {dimsLabel(computed)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{computed.qty}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {fmtKg(computed.actual_weight)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {fmtKg(computed.volumetric_weight)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {fmt(computed.cbm, 3)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold text-secondary">
                    {fmtKg(computed.chargeable_weight)} kg
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold text-secondary">
                    ₹{parcelFreightAmount(computed, ratePerKg).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] sm:text-xs text-gray-400 leading-relaxed">
        Chargeable weight is the higher of actual vs volumetric weight per package.
      </p>
    </PackageDimensionModalShell>
  );
};

export default ViewPackageDimensionModal;
